/** Sound profiles: which library sound each game event plays. Stored on the server, chosen per device. */
import { apiJson } from '../auth/session.ts';
import { SOUND_EVENT_BY_ID } from './events.ts';
import type { SoundLibrary } from './library.ts';

export interface SoundAssignment {
  /** library sound id, or null for silence */
  sound: string | null;
  volume: number;
  pitch: number;
}

export interface SoundProfile {
  id: string;
  name: string;
  ownerId: string;
  ownerEmail: string | null;
  basedOn: string | null;
  createdAt: string;
  updatedAt: string;
  sfxVolume: number;
  musicVolume: number;
  events: Record<string, SoundAssignment>;
}

export const DEFAULT_PROFILE_ID = 'default';
const SELECTED_KEY = 'ple:soundProfile';

/** Where the built-in profile differs from "first suggestion at full volume": matches what the game already played. */
const DEFAULT_OVERRIDES: Record<string, Partial<SoundAssignment>> = {
  dash: { sound: 'orig-jump', volume: 0.8, pitch: 2.5 },
  death: { sound: 'orig-death-1' },
  jetpack: { sound: 'orig-jetpack-thrust-v2' },
  squish: { sound: 'splat-impactsplat01-mp3', volume: 0.7 },
  levelStart: { sound: 'nesm-intro-jingle', volume: 0.7 },
  footstep: { volume: 0.3 },
  land: { volume: 0.5 },
  wallSlide: { volume: 0.4 },
  pushBlock: { volume: 0.5 },
  dashReady: { volume: 0.4 },
  timerTick: { volume: 0.4 },
  uiMove: { volume: 0.5 },
  platformMove: { volume: 0.5 },
  tickTock: { volume: 0.6 },
};

export function defaultAssignment(lib: SoundLibrary | null, event: string): SoundAssignment {
  const o = DEFAULT_OVERRIDES[event] ?? {};
  const sound = o.sound !== undefined ? o.sound : lib?.suggestions[event]?.[0] ?? null;
  return { sound, volume: o.volume ?? 1, pitch: o.pitch ?? 1 };
}

export function resolveAssignment(lib: SoundLibrary | null, profile: SoundProfile | null, event: string): SoundAssignment {
  const own = profile?.events[event];
  if (own && (own.sound === null || !lib || lib.byId.has(own.sound))) return own;
  return defaultAssignment(lib, event);
}

export function isMusicEvent(event: string): boolean {
  return SOUND_EVENT_BY_ID.get(event)?.kind === 'music';
}

export function profileOwner(p: SoundProfile): string {
  if (p.id === DEFAULT_PROFILE_ID) return 'Built in';
  return p.ownerEmail ?? p.ownerId;
}

// ---------- server ----------

export async function listProfiles(): Promise<SoundProfile[]> {
  return (await apiJson<{ profiles: SoundProfile[] }>('GET', '/api/sound-profiles')).profiles;
}

export async function fetchProfile(id: string): Promise<SoundProfile> {
  return (await apiJson<{ profile: SoundProfile }>('GET', `/api/sound-profiles/${encodeURIComponent(id)}`)).profile;
}

export async function copyProfile(id: string, name?: string): Promise<SoundProfile> {
  return (await apiJson<{ profile: SoundProfile }>('POST', `/api/sound-profiles/${encodeURIComponent(id)}/copy`, name ? { name } : {})).profile;
}

export async function saveProfile(p: Pick<SoundProfile, 'id' | 'name' | 'events' | 'sfxVolume' | 'musicVolume'>): Promise<SoundProfile> {
  const body = { name: p.name, events: p.events, sfxVolume: p.sfxVolume, musicVolume: p.musicVolume };
  const saved = (await apiJson<{ profile: SoundProfile }>('PUT', `/api/sound-profiles/${encodeURIComponent(p.id)}`, body)).profile;
  if (saved.id === selectedProfileId()) activeCache = Promise.resolve(saved);
  return saved;
}

export async function deleteProfile(id: string): Promise<void> {
  await apiJson('DELETE', `/api/sound-profiles/${encodeURIComponent(id)}`);
  if (selectedProfileId() === id) selectProfile(DEFAULT_PROFILE_ID);
}

// ---------- the profile this device plays with ----------

const listeners = new Set<() => void>();
let activeCache: Promise<SoundProfile | null> | null = null;

export function selectedProfileId(): string {
  try {
    return globalThis.localStorage?.getItem(SELECTED_KEY) || DEFAULT_PROFILE_ID;
  } catch {
    return DEFAULT_PROFILE_ID;
  }
}

export function selectProfile(id: string) {
  try {
    globalThis.localStorage?.setItem(SELECTED_KEY, id);
  } catch {
    /* private mode */
  }
  activeCache = null;
  for (const fn of listeners) fn();
}

export function onSelectedProfile(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** null means the built-in default. A deleted or unreachable profile also falls back to it. */
export function activeProfile(): Promise<SoundProfile | null> {
  const id = selectedProfileId();
  if (id === DEFAULT_PROFILE_ID) return Promise.resolve(null);
  activeCache ??= fetchProfile(id).catch(() => {
    activeCache = null;
    return null;
  });
  return activeCache;
}
